import React, { useState, useEffect, useRef, useCallback } from "react";
import { Box, Typography, Text, Pressable, Button } from "components/ui";
import { useIsMobile } from "utils/hooks/useIsMobile";
import { DateRangePicker, Range, StaticRange } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import { Calendar } from "lucide-react-native";
import { addDays, endOfDay, startOfDay, startOfMonth, endOfMonth, addMonths, startOfWeek, endOfWeek, startOfQuarter, endOfQuarter, startOfYear } from "date-fns";

interface DateRange {
	startDate: Date;
	endDate: Date;
	key?: string;
}

interface CustomRange {
	label: string;
	range: () => DateRange;
}

interface EnhancedDateSelectProps {
	isDateRange?: boolean;
	monthOnly?: boolean;
	onDateChange: (dates: DateRange) => void;
	initialStartDate?: Date;
	initialEndDate?: Date;
}

const defined = {
	startOfWeek: startOfWeek(new Date()),
	endOfWeek: endOfWeek(new Date()),
	startOfLastWeek: startOfWeek(addDays(new Date(), -7)),
	endOfLastWeek: endOfWeek(addDays(new Date(), -7)),
	startOfToday: startOfDay(new Date()),
	endOfToday: endOfDay(new Date()),
	startOfYesterday: startOfDay(addDays(new Date(), -1)),
	endOfYesterday: endOfDay(addDays(new Date(), -1)),
	startOfMonth: startOfMonth(new Date()),
	endOfMonth: endOfMonth(new Date()),
	startOfLastMonth: startOfMonth(addMonths(new Date(), -1)),
	endOfLastMonth: endOfMonth(addMonths(new Date(), -1)),
	startOfThisQuarter: startOfQuarter(new Date()),
	endOfThisQuarter: endOfQuarter(new Date()),
	startOfLastQuarter: startOfQuarter(addMonths(new Date(), -3)),
	endOfLastQuarter: endOfQuarter(addMonths(new Date(), -3)),
	startOfYear: startOfYear(new Date()),
	startOfFinancialYear: new Date().getMonth() <= 6 ? new Date(new Date().getFullYear() - 1, 6, 1) : new Date(new Date().getFullYear(), 6, 1),
};

// Create static ranges that match the StaticRange interface from react-date-range
const createStaticRanges = (ranges: CustomRange[]): StaticRange[] => {
	return ranges.map((range) => ({
		label: range.label,
		range: range.range,
		isSelected(rangeObj: Range) {
			const definedRange = range.range();
			return rangeObj.startDate instanceof Date && rangeObj.endDate instanceof Date && definedRange.startDate.getTime() === rangeObj.startDate.getTime() && definedRange.endDate.getTime() === rangeObj.endDate.getTime();
		},
	}));
};

const predefinedRanges: CustomRange[] = [
	{
		label: "Yesterday",
		range: () => ({
			startDate: defined.startOfYesterday,
			endDate: defined.endOfYesterday,
		}),
	},
	{
		label: "This Week",
		range: () => ({
			startDate: defined.startOfWeek,
			endDate: defined.endOfWeek,
		}),
	},
	{
		label: "Last Week",
		range: () => ({
			startDate: defined.startOfLastWeek,
			endDate: defined.endOfLastWeek,
		}),
	},
	{
		label: "This Month",
		range: () => ({
			startDate: defined.startOfMonth,
			endDate: defined.endOfMonth,
		}),
	},
	{
		label: "Last Month",
		range: () => ({
			startDate: defined.startOfLastMonth,
			endDate: defined.endOfLastMonth,
		}),
	},
	{
		label: "This Quarter",
		range: () => ({
			startDate: defined.startOfThisQuarter,
			endDate: defined.endOfThisQuarter,
		}),
	},
	{
		label: "Last Quarter",
		range: () => ({
			startDate: defined.startOfLastQuarter,
			endDate: defined.endOfLastQuarter,
		}),
	},
	{
		label: "Year to Date",
		range: () => ({
			startDate: defined.startOfYear,
			endDate: defined.endOfToday,
		}),
	},
	{
		label: "Financial Year to Date",
		range: () => ({
			startDate: defined.startOfFinancialYear,
			endDate: defined.endOfToday,
		}),
	},
];

const formatDate = (date: Date | null): string => {
	if (!date) return "";
	const options: Intl.DateTimeFormatOptions = { day: "2-digit", month: "2-digit", year: "numeric" };
	return date.toLocaleDateString("en-GB", options);
};

const EnhancedDateSelect = ({ isDateRange = true, monthOnly = false, onDateChange, initialStartDate, initialEndDate }: EnhancedDateSelectProps) => {
	const isMobile = useIsMobile();
	const [isOpen, setIsOpen] = useState(false);
	const [selectedRange, setSelectedRange] = useState<DateRange>({
		startDate: initialStartDate || defined.startOfLastMonth,
		endDate: initialEndDate || defined.endOfLastMonth,
		key: "selection",
	});
	const [selectedPredefinedRange, setSelectedPredefinedRange] = useState<CustomRange | null>(null);
	const dateRangeWrapperRef = useRef(null);

	// Find the matching predefined range on initialization and when selection changes
	useEffect(() => {
		findSelectedPredefinedRange();
	}, [selectedRange.startDate, selectedRange.endDate]);

	// Notify parent component when selection changes
	useEffect(() => {
		if (onDateChange) {
			onDateChange({
				startDate: selectedRange.startDate,
				endDate: selectedRange.endDate,
			});
		}
	}, [selectedRange, onDateChange]);

	const findSelectedPredefinedRange = () => {
		const matchingRange = predefinedRanges.find((staticRange) => {
			const range = staticRange.range();
			return selectedRange.startDate && selectedRange.endDate && range.startDate.getTime() === selectedRange.startDate.getTime() && range.endDate.getTime() === selectedRange.endDate.getTime();
		});

		setSelectedPredefinedRange(matchingRange || null);
	};

	const handleSelect = (ranges: any) => {
		if (ranges.selection) {
			setSelectedRange(ranges.selection);
		}
	};

	const handleOpenCalendar = () => {
		setIsOpen(true);
	};

	const handleCloseCalendar = () => {
		setIsOpen(false);
	};

	const getDisplayValue = () => {
		if (selectedPredefinedRange) {
			return selectedPredefinedRange.label;
		}

		if (monthOnly) {
			return selectedRange.startDate.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
		}

		if (selectedRange.startDate.getTime() === selectedRange.endDate.getTime()) {
			return formatDate(selectedRange.startDate);
		}

		return `${formatDate(selectedRange.startDate)} to ${formatDate(selectedRange.endDate)}`;
	};

	// Handle click outside to close the calendar
	const handleClickOutside = useCallback(
		(e: any) => {
			if (isOpen && dateRangeWrapperRef.current) {
				// @ts-ignore
				if (dateRangeWrapperRef.current && !dateRangeWrapperRef.current.contains(e.target)) {
					handleCloseCalendar();
				}
			}
		},
		[isOpen],
	);

	useEffect(() => {
		if (typeof document !== "undefined" && isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
			return () => {
				document.removeEventListener("mousedown", handleClickOutside);
			};
		}
	}, [isOpen, handleClickOutside]);

	return (
		<Box position="relative" width="100%">
			<Box flex={1}>
				<Button
					flex={1}
					onPress={handleOpenCalendar}
					w="100%"
					flexDirection="row"
					justifyContent="space-between"
					alignItems="center"
					padding={10}
					paddingVertical={12}
					backgroundColor="white"
					borderRadius={12}
					borderColor="$neutral300"
					borderWidth={1}
				>
					{/* <Box > */}
					<Typography.ParagraphSmall weight="bold">{getDisplayValue()}</Typography.ParagraphSmall>
					<Calendar size={24} />
					{/* </Box> */}
				</Button>
			</Box>

			{isOpen && (
				<Box
					position="absolute"
					zIndex={999}
					backgroundColor="white"
					style={{
						shadowColor: "#000",
						shadowOffset: { width: 0, height: 2 },
						shadowOpacity: 0.1,
						shadowRadius: 10,
						elevation: 5,
						top: "100%",
					}}
					ref={dateRangeWrapperRef}
				>
					{/* @ts-ignore - DateRangePicker has type incompatibilities with JSX */}
					<DateRangePicker
						ranges={[selectedRange]}
						onChange={handleSelect}
						staticRanges={createStaticRanges(predefinedRanges)}
						inputRanges={[]}
						maxDate={new Date()}
						months={isMobile ? 1 : 2}
						direction="horizontal"
						moveRangeOnFirstSelection={false}
					/>

					<Box w="100%" padding={2} alignItems="center">
						<Button w="100%" variant="primary" onPress={handleCloseCalendar}>
							<Text style={{ color: "white" }}>Done</Text>
						</Button>
					</Box>
				</Box>
			)}
		</Box>
	);
};

export default EnhancedDateSelect;
